import React, { useState } from 'react';
import { CriterionResult, ScoringTraceability } from '../types';
import { useTranslation } from '../../../i18n';

interface CompetencyBreakdownCardProps {
  criteria?: CriterionResult[];
  scoringTraceability?: ScoringTraceability;
  showTraceability?: boolean;
}

export const CompetencyBreakdownCard: React.FC<CompetencyBreakdownCardProps> = ({
  criteria = [],
  scoringTraceability,
  showTraceability = false,
}) => {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const displayCriteria = criteria;

  return (
    <div
      className="scorecard-card"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: '14px',
        padding: 'clamp(14px, 3vw, 22px) clamp(12px, 3vw, 24px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'grid',
        gap: '16px',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header with Star SVG Icon */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '7px',
              background: '#FEF3C7',
              color: '#D97706',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
            </svg>
          </span>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: '15.5px', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            {t('scorecard.competency_breakdown_title', undefined, 'Competency breakdown')}
          </h2>
        </div>

        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {t('scorecard.scored_against_rubric', undefined, "Scored against your organisation's rubric")}
        </span>
      </div>

      {/* Benchmark Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap', fontSize: '11px', color: 'var(--muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#10B981' }} />
          <span>{t('scorecard.legend_strong', undefined, 'Strong (4.0+)')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#F59E0B' }} />
          <span>{t('scorecard.legend_ontrack', undefined, 'On-track (3.0 - 3.9)')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#EF4444' }} />
          <span>{t('scorecard.legend_needswork', undefined, 'Needs work (below 3.0)')}</span>
        </div>
      </div>

      {/* Criteria Rows */}
      <div style={{ display: 'grid', gap: '14px' }}>
        {displayCriteria.length === 0 ? (
          <div style={{ padding: '18px', background: '#F8FAFC', borderRadius: '10px', border: '1px dashed var(--line)', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)' }}>
              {t('scorecard.no_competencies_evaluated', undefined, 'No competency rubrics were evaluated for this session.')}
            </p>
          </div>
        ) : (
          displayCriteria.map((item, idx) => {
          const isExpanded = expandedIndex === idx;
          const max = item.maxScore || 5;
          const rawScore = typeof item.score === 'number' ? item.score : (item.evidenceStatus === 'CRITERIA_NOT_MET' ? 0 : item.score);
          const score = typeof rawScore === 'number' ? rawScore : 0;
          const scoreRatio = max > 0 ? score / max : 0;
          const scoreColor = scoreRatio >= 0.8 ? '#10B981' : scoreRatio >= 0.6 ? '#F59E0B' : '#EF4444';
          const scoreFormatted = `${score} / ${max}`;
          const noteText = item.rationale || item.explanation;

          return (
            <div
              key={idx}
              style={{
                display: 'grid',
                gap: '8px',
                paddingBottom: idx < displayCriteria.length - 1 ? '14px' : '0',
                borderBottom: idx < displayCriteria.length - 1 ? '1px solid var(--line)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: '1 1 180px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--navy)', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {item.name}
                  </span>
                  {item.weightPercentage > 0 && (
                    <span style={{ fontSize: '11px', color: 'var(--muted)', background: 'var(--surface)', padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                      Weight: {item.weightPercentage}%
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: scoreColor }}>
                    {scoreFormatted}
                  </span>
                </div>
              </div>

              {/* Progress Track with Benchmark Indicators */}
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '6px',
                  background: '#F3F4F6',
                  borderRadius: '999px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: score > 0 ? `${Math.min(100, Math.round(scoreRatio * 100))}%` : '0%',
                    background: scoreColor,
                    borderRadius: '999px',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>

              {/* Rationale / Explanation */}
              {noteText && (
                <div
                  onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                  style={{ fontSize: '12px', color: 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}
                >
                  <span style={{ lineHeight: 1.4, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: isExpanded ? 'unset' : 3, overflow: 'hidden', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {/^(assess|evaluate|check|verify|rate|review|grade|measure|ensure|determine|observe|score)\b/i.test(noteText.trim()) || noteText.includes('###')
                      ? `Learner demonstrated ${item.name.toLowerCase()} in dialogue.`
                      : noteText}
                  </span>
                </div>
              )}
            </div>
          );
        }))}
      </div>

      {/* Scoring Traceability Section */}
      {showTraceability && scoringTraceability && (
        <div style={{ marginTop: '10px', padding: '14px', background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--line)', minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
            <strong style={{ fontSize: '12.5px', color: 'var(--navy)' }}>
              📐 {t('scorecard.score_calc_traceability', undefined, 'Score Calculation & Weight Traceability')}
            </strong>
            <span style={{ fontSize: '11px', color: 'var(--muted)', fontFamily: 'monospace', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
              {t('scorecard.formula_label', undefined, 'Formula:')} {scoringTraceability.formula || 'Σ(weight × (score / maxScore)) × 100'}
            </span>
          </div>

          {scoringTraceability.competencies && scoringTraceability.competencies.length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: '8px', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', minWidth: '380px', fontSize: '11.5px', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--line)', color: 'var(--muted)' }}>
                    <th style={{ padding: '4px 6px' }}>{t('scorecard.table_competency', undefined, 'Competency')}</th>
                    <th style={{ padding: '4px 6px', textAlign: 'center' }}>{t('scorecard.table_raw_score', undefined, 'Raw Score')}</th>
                    <th style={{ padding: '4px 6px', textAlign: 'center' }}>{t('scorecard.table_weight', undefined, 'Weight')}</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>{t('scorecard.table_contribution', undefined, 'Contribution')}</th>
                  </tr>
                </thead>
                <tbody>
                    {scoringTraceability.competencies.map((comp, cIdx) => {
                      const rawWeight = comp.weight ?? comp.weightPercentage ?? (comp.normalizedWeight != null ? Math.round(Number(comp.normalizedWeight) * 100) : null);
                      let formattedWeight = '—';
                      if (rawWeight != null && rawWeight !== '') {
                        const str = String(rawWeight).trim();
                        if (str.endsWith('%')) {
                          formattedWeight = str;
                        } else {
                          const num = parseFloat(str);
                          if (!isNaN(num)) {
                            const pct = (num > 0 && num <= 1) ? Math.round(num * 100) : num;
                            formattedWeight = `${pct}%`;
                          } else {
                            formattedWeight = str;
                          }
                        }
                      }

                      return (
                        <tr key={cIdx} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                          <td style={{ padding: '4px 6px', color: 'var(--navy)', fontWeight: 600 }}>{comp.name}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'center', color: 'var(--ink)' }}>{comp.score} / {comp.maxScore}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'center', color: 'var(--muted)' }}>{formattedWeight}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: 'var(--blue)' }}>{comp.contribution != null && !isNaN(Number(comp.contribution)) ? `${parseFloat(String(comp.contribution)).toFixed(1)}%` : '—'}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', borderTop: '1px solid var(--line)', paddingTop: '8px', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <span style={{ color: 'var(--muted)' }}>{t('scorecard.total_calc_score', undefined, 'Total Calculated Score:')} </span>
              <strong style={{ color: 'var(--navy)' }}>{scoringTraceability.overallScore}%</strong>
              <span style={{ color: 'var(--muted)', marginLeft: '8px' }}>({t('scorecard.pass_threshold', { score: scoringTraceability.passMark }, `Pass Threshold: ${scoringTraceability.passMark}%`)})</span>
            </div>
            {scoringTraceability.decisionRationale && (
              <span style={{ fontSize: '11.5px', color: scoringTraceability.isPassed ? 'var(--ok)' : 'var(--bad)', fontWeight: 600 }}>
                {scoringTraceability.decisionRationale}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
